import { useLocation } from 'react-router-dom'
import { StoreHeader } from '@/store/components/layout/StoreHeader'
import { StoreFooter } from '@/store/components/layout/StoreFooter'
import {
  HomePageSkeleton,
  OrdersListSkeleton,
  ProductDetailPageSkeleton,
  ShopPageSkeleton,
  SearchPageSkeleton,
  StoreLegalPageSkeleton,
  StoreOrderDetailPageSkeleton,
  StoreSkeleton,
  WishlistPageSkeleton,
} from '@/store/components/StoreSkeletons'

function isAdminPath(pathname: string) {
  return pathname.startsWith('/admin')
}

function StorePageLoaderBody() {
  const { pathname } = useLocation()

  if (pathname === '/' || pathname === '') {
    return <HomePageSkeleton />
  }

  if (/^\/shop\/[^/]+$/.test(pathname)) {
    return <ProductDetailPageSkeleton />
  }

  if (pathname === '/shop' || pathname.startsWith('/shop?')) {
    return (
      <div className="store-container py-10">
        <ShopPageSkeleton />
      </div>
    )
  }

  if (pathname === '/search') {
    return <SearchPageSkeleton />
  }

  if (pathname === '/wishlist') {
    return (
      <div className="store-container py-10">
        <StoreSkeleton className="h-9 w-32 rounded-lg mb-8" />
        <WishlistPageSkeleton />
      </div>
    )
  }

  if (pathname === '/orders') {
    return (
      <div className="store-container py-10">
        <StoreSkeleton className="h-9 w-36 rounded-lg mb-2" />
        <StoreSkeleton className="h-4 w-72 max-w-full rounded-md mb-8" />
        <div className="glass-card gold-border-glow p-6 max-w-lg space-y-4 mb-10">
          <StoreSkeleton className="h-10 w-full rounded-xl" />
          <StoreSkeleton className="h-10 w-full rounded-xl" />
          <StoreSkeleton className="h-11 w-36 rounded-xl" />
        </div>
        <OrdersListSkeleton count={5} />
      </div>
    )
  }

  if (/^\/orders\/[^/]+$/.test(pathname)) {
    return <StoreOrderDetailPageSkeleton />
  }

  if (
    pathname === '/privacy-policy' ||
    pathname === '/terms-and-conditions' ||
    pathname === '/return-refund-policy'
  ) {
    return (
      <div className="store-container py-10 md:py-14">
        <StoreSkeleton className="h-4 w-28 rounded-md mb-8" />
        <div className="glass-card gold-border-glow p-6 md:p-10 max-w-3xl mx-auto">
          <div className="flex items-start gap-4 mb-8">
            <StoreSkeleton className="h-12 w-12 shrink-0 rounded-xl" />
            <div className="space-y-2 flex-1">
              <StoreSkeleton className="h-8 w-48 rounded-lg" />
              <StoreSkeleton className="h-4 w-32 rounded-md" />
            </div>
          </div>
          <StoreLegalPageSkeleton />
        </div>
      </div>
    )
  }

  if (pathname === '/cart') {
    return (
      <div className="store-container py-10">
        <StoreSkeleton className="h-9 w-44 rounded-lg mb-8" />
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <div
                key={i}
                className="glass-card gold-border-glow p-4 flex gap-4"
              >
                <StoreSkeleton className="h-24 w-20 shrink-0 rounded-xl" />
                <div className="flex-1 space-y-3">
                  <StoreSkeleton className="h-5 w-2/3 rounded-md" />
                  <StoreSkeleton className="h-4 w-24 rounded-md" />
                  <StoreSkeleton className="h-9 w-28 rounded-lg" />
                </div>
              </div>
            ))}
          </div>
          <div className="glass-card gold-border-glow p-6 space-y-4 h-fit">
            <StoreSkeleton className="h-6 w-32 rounded-md" />
            <StoreSkeleton className="h-4 w-full rounded-md" />
            <StoreSkeleton className="h-11 w-full rounded-xl" />
          </div>
        </div>
      </div>
    )
  }

  if (pathname === '/contact') {
    return (
      <div className="store-container py-10 md:py-14 max-w-2xl">
        <StoreSkeleton className="h-9 w-40 rounded-lg mb-2" />
        <StoreSkeleton className="h-4 w-full max-w-md rounded-md mb-8" />
        <div className="glass-card gold-border-glow p-6 md:p-8 space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <StoreSkeleton key={i} className="h-10 w-full rounded-xl" />
          ))}
          <StoreSkeleton className="h-28 w-full rounded-xl" />
          <StoreSkeleton className="h-11 w-32 rounded-xl" />
        </div>
      </div>
    )
  }

  return <HomePageSkeleton />
}

/** Storefront shell + route-matched skeleton for initial load and lazy route chunks. */
export function StorePageLoader() {
  return (
    <div className="flex min-h-screen flex-col">
      <StoreHeader />
      <main className="flex-1">
        <StorePageLoaderBody />
      </main>
      <StoreFooter />
    </div>
  )
}

export function resolveStorePageLoader(pathname: string) {
  return !isAdminPath(pathname)
}
